import React from 'react';
import { useNavigate } from 'react-router-dom';
import { IMAGES, DISTRICTS } from '../mock/data';
import { Reveal, SectionHeading, Eyebrow } from '../components/xc/Bits';
import { InvestorNote } from '../components/xc/InvestorNote';
import {
  ArrowRight, Gamepad2, Building2, Users, Coins, Trophy, ShieldCheck,
  Dice5, Landmark, Car, Home, Swords, ScanFace, Radar, Lock, ChevronRight,
} from 'lucide-react';

function WaysCard({ icon: Icon, title, text, cta, onClick, accent }) {
  return (
    <div className="xc-card rounded-2xl p-7 flex flex-col h-full">
      <div className="h-12 w-12 rounded-xl grid place-items-center mb-5" style={{ background: `${accent}18`, border: `1px solid ${accent}44` }}>
        <Icon className="h-6 w-6" style={{ color: accent }} />
      </div>
      <h3 className="font-display font-700 text-xl text-white tracking-wide">{title}</h3>
      <p className="mt-3 text-white/55 text-sm leading-relaxed flex-1">{text}</p>
      <button onClick={onClick} className="btn-ghost mt-6 rounded-lg px-4 py-2.5 text-sm font-600 flex items-center justify-center gap-2 group">
        {cta} <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
      </button>
    </div>
  );
}

function FlowNode({ icon: Icon, label, color }) {
  return (
    <div className="flex flex-col items-center text-center">
      <div className="h-14 w-14 rounded-xl grid place-items-center xc-glass" style={{ borderColor: `${color}55` }}>
        <Icon className="h-6 w-6" style={{ color }} />
      </div>
      <span className="mt-2 text-xs text-white/70 font-500 max-w-[110px]">{label}</span>
    </div>
  );
}

function LiveCityPulse() {
  const [stats, setStats] = React.useState({ players: 2418, missions: 37, crews: 128 });
  React.useEffect(() => {
    const t = setInterval(() => {
      setStats((s) => ({
        players: Math.max(2200, s.players + Math.floor(Math.random() * 17) - 7),
        missions: Math.max(30, s.missions + (Math.random() > 0.7 ? (Math.random() > 0.5 ? 1 : -1) : 0)),
        crews: Math.max(110, s.crews + (Math.random() > 0.8 ? 1 : 0)),
      }));
    }, 3500);
    return () => clearInterval(t);
  }, []);
  const item = (v, label) => (
    <div className="flex items-baseline gap-1.5">
      <span className="font-display font-700 text-white text-sm tabular-nums">{v.toLocaleString()}</span>
      <span className="text-white/45 text-xs">{label}</span>
    </div>
  );
  return (
    <div data-testid="live-city-pulse" className="mt-6 inline-flex flex-wrap items-center gap-x-5 gap-y-2 xc-glass rounded-xl px-4 py-2.5">
      <span className="flex items-center gap-2 text-[11px] font-600 tracking-widest text-[#FF204E]">
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#FF204E] opacity-60" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-[#FF204E]" />
        </span>
        LIVE
      </span>
      {item(stats.players, 'players in the city')}
      {item(stats.missions, 'missions running')}
      {item(stats.crews, 'crews active')}
    </div>
  );
}

export default function Landing() {
  const nav = useNavigate();
  const flow = [
    { icon: Dice5, label: 'Casino Game', color: '#FF204E' },
    { icon: ShieldCheck, label: 'Trusted Game Activity', color: '#B026FF' },
    { icon: Building2, label: 'Xincity Progress', color: '#B026FF' },
    { icon: Users, label: 'Crew Mission', color: '#FF204E' },
    { icon: Coins, label: 'Loyalty Coins', color: '#E0B85A' },
    { icon: Trophy, label: 'Status & Property', color: '#E0B85A' },
  ];
  const remembers = [
    { k: 'Level', v: '18' }, { k: 'Crew', v: 'The Syndicate' }, { k: 'Property', v: 'Downtown Apartment' },
    { k: 'Missions Completed', v: '27' }, { k: 'Crew Victories', v: '15' }, { k: 'Vehicle', v: 'Obsidian V12' },
  ];
  const provider = [
    { icon: Gamepad2, label: 'Provider Game' }, { icon: ShieldCheck, label: 'Settled Game Event' },
    { icon: Landmark, label: 'Casino Platform' }, { icon: Building2, label: 'Xincity' },
  ];
  const safe = [
    { icon: ShieldCheck, label: 'Responsible Gambling', color: '#2FBF71', to: '/safe-play' },
    { icon: ScanFace, label: 'KYC', color: '#B026FF', to: '/trust' },
    { icon: Radar, label: 'AML Monitoring', color: '#FF204E', to: '/trust' },
    { icon: Lock, label: 'Fraud Protection', color: '#E0B85A', to: '/trust' },
    { icon: Radar, label: 'Xintinel Monitoring', color: '#B026FF', to: '/trust' },
  ];

  return (
    <div className="bg-[#070609]">
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMAGES.skyline} alt="Xincity neon skyline at night — futuristic casino city" className="h-full w-full object-cover" fetchPriority="high" />
          <div className="absolute inset-0 city-overlay" />
          <div className="absolute inset-0 vignette" />
          <div className="absolute inset-0 xc-grid opacity-40" />
        </div>
        <div className="relative max-w-[1440px] mx-auto px-4 lg:px-8 w-full py-16 md:py-24">
          <div className="max-w-3xl xc-fade-up">
            <Eyebrow>Welcome to Xincity</Eyebrow>
            <h1 className="font-display font-700 text-4xl sm:text-5xl md:text-7xl leading-[1.02] md:leading-[0.98] tracking-tight text-white">
              Play the casino.<br />Build your crew.<br /><span className="text-gradient-rp">Own the city.</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-white/65 max-w-xl leading-relaxed">
              A persistent multiplayer world built around real online casino games. Play normally, explore Xincity alone, or form a crew with real players and complete cinematic missions together.
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <button onClick={() => nav('/city')} className="btn-neon rounded-xl px-7 py-4 font-600 tracking-wide flex items-center gap-2 group">
                ENTER XINCITY <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </button>
              <button onClick={() => nav('/casino')} className="btn-ghost rounded-xl px-7 py-4 font-600 tracking-wide">JUST PLAY</button>
            </div>
            <div className="mt-10 flex items-center gap-3 text-xs text-white/40">
              <span className="h-6 w-6 grid place-items-center rounded-full border border-[#E0B85A]/40 text-[#E0B85A] font-600">18</span>
              Investor prototype — no real money, no deposits, no real gambling. Demo data throughout.
            </div>
            <LiveCityPulse />
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-[#070609] to-transparent" />
      </section>

      {/* THREE WAYS */}
      <section className="max-w-[1440px] mx-auto px-4 lg:px-8 py-14 md:py-20">
        <Reveal><SectionHeading eyebrow="Three ways to play" title="Your city. Your rules." sub="Xincity enriches casino play — it never requires it. Move freely between all three modes at any time." center /></Reveal>
        <Reveal delay={60}>
          <div className="flex justify-center mt-5">
            <InvestorNote id="three-ways" title="Why optionality matters">
              Forcing social features on casino players increases churn. By making the city layer strictly optional, XINCITY captures the ~20% of players who want deep engagement without alienating the majority who just want to play. Retention uplift without acquisition risk.
            </InvestorNote>
          </div>
        </Reveal>
        <div className="grid md:grid-cols-3 gap-6 mt-12">
          <Reveal delay={0}><WaysCard icon={Gamepad2} accent="#FF204E" title="JUST PLAY" text="Play casino games normally. No missions, no crew and no social interaction required." cta="PLAY CASINO" onClick={() => nav('/casino')} /></Reveal>
          <Reveal delay={90}><WaysCard icon={Building2} accent="#B026FF" title="SOLO XINCITY" text="Explore the city, build your identity, earn rewards and progress at your own pace." cta="EXPLORE XINCITY" onClick={() => nav('/city')} /></Reveal>
          <Reveal delay={180}><WaysCard icon={Users} accent="#E0B85A" title="MULTIPLAYER" text="Join real players, form crews, make decisions together and complete cinematic missions." cta="VIEW MISSIONS" onClick={() => nav('/missions')} /></Reveal>
        </div>
        <Reveal delay={240}>
          <div data-testid="district-quick-links" className="mt-10 flex flex-wrap items-center justify-center gap-3">
            <span className="text-xs tracking-widest uppercase text-white/35 font-600 mr-1">Jump into a district</span>
            {DISTRICTS.filter((d) => !d.locked).map((d) => (
              <button
                key={d.id}
                data-testid={`district-link-${d.id}`}
                onClick={() => nav(`/district/${d.id}`)}
                className="xc-glass rounded-full px-4 py-2 text-sm text-white/75 font-500 flex items-center gap-2 hover:text-white hover:border-[#FF204E]/50 transition-colors"
              >
                {d.name}
                <span className="text-[11px] text-[#B026FF] font-600">{d.players} online</span>
              </button>
            ))}
          </div>
        </Reveal>
      </section>

      {/* HOW IT WORKS */}
      <section className="relative py-14 md:py-20 border-y border-white/5 overflow-hidden">
        <div className="absolute inset-0 opacity-30"><img src={IMAGES.purpleCity} alt="" loading="lazy" className="h-full w-full object-cover" /><div className="absolute inset-0 bg-[#070609]/85" /></div>
        <div className="relative max-w-[1440px] mx-auto px-4 lg:px-8">
          <Reveal><SectionHeading eyebrow="How Xincity works" color="#B026FF" title="From a single spin to your own empire" sub="Casino activity becomes a shared multiplayer story — no technical knowledge required." center /></Reveal>
          <Reveal delay={120}>
            <div className="mt-14 flex flex-wrap items-center justify-center gap-3 md:gap-5">
              {flow.map((f, i) => (
                <React.Fragment key={i}>
                  <FlowNode {...f} />
                  {i < flow.length - 1 && <ChevronRight className="h-5 w-5 text-white/25 hidden md:block" />}
                </React.Fragment>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* THE WORLD REMEMBERS */}
      <section className="max-w-[1440px] mx-auto px-4 lg:px-8 py-14 md:py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <SectionHeading eyebrow="The world remembers" color="#E0B85A" title="A persistent identity that outlives every session" sub="Your progress, identity, crew and assets remain part of Xincity between casino sessions." />
          </Reveal>
          <Reveal delay={120}>
            <div className="grid grid-cols-2 gap-4">
              {remembers.map((r, i) => (
                <div key={i} className="xc-glass rounded-xl p-5">
                  <div className="text-xs text-white/45 tracking-wide uppercase">{r.k}</div>
                  <div className="mt-1 font-display font-600 text-lg text-white">{r.v}</div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* BUILT AROUND REAL CASINO GAMES */}
      <section className="border-y border-white/5 bg-[#0D0B10] py-14 md:py-20">
        <div className="max-w-[1440px] mx-auto px-4 lg:px-8">
          <Reveal><SectionHeading eyebrow="Built around real casino games" title="We never touch the maths" sub="In production, casino games are supplied by licensed game providers. Xincity does not modify reels, mathematics, RNG or game outcomes. Xincity reacts to trusted game events and adds social progression around them." center /></Reveal>
          <Reveal delay={60}>
            <div className="flex justify-center mt-5">
              <InvestorNote id="no-maths" title="Why this is the regulatory moat">
                Because XINCITY only consumes settled game events and never alters RNG or RTP, it sits outside the certified game loop. That means no game re-certification, faster market entry per jurisdiction, and provider partnerships instead of provider competition.
              </InvestorNote>
            </div>
          </Reveal>
          <Reveal delay={120}>
            <div className="mt-12 flex flex-wrap items-center justify-center gap-3 md:gap-6">
              {provider.map((p, i) => (
                <React.Fragment key={i}>
                  <div className="xc-card rounded-xl px-6 py-5 flex items-center gap-3">
                    <p.icon className="h-5 w-5 text-[#B026FF]" />
                    <span className="text-sm text-white/80 font-500">{p.label}</span>
                  </div>
                  {i < provider.length - 1 && <ChevronRight className="h-5 w-5 text-white/25" />}
                </React.Fragment>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* SAFE BY DESIGN */}
      <section className="max-w-[1440px] mx-auto px-4 lg:px-8 py-14 md:py-20">
        <Reveal><SectionHeading eyebrow="Safe by design" color="#2FBF71" title="Trust is the foundation" sub="Compliance and player protection are built into the architecture from day one." center /></Reveal>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-12">
          {safe.map((s, i) => (
            <Reveal key={i} delay={i * 70}>
              <button onClick={() => nav(s.to)} className="xc-card rounded-xl p-6 w-full text-center flex flex-col items-center gap-3 h-full">
                <div className="h-11 w-11 rounded-lg grid place-items-center" style={{ background: `${s.color}16`, border: `1px solid ${s.color}40` }}>
                  <s.icon className="h-5 w-5" style={{ color: s.color }} />
                </div>
                <span className="text-sm text-white/80 font-500">{s.label}</span>
              </button>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-16 md:py-24 overflow-hidden">
        <div className="absolute inset-0"><img src={IMAGES.rainyStreet} alt="" loading="lazy" className="h-full w-full object-cover" /><div className="absolute inset-0 bg-[#070609]/80" /><div className="absolute inset-0 city-overlay" /></div>
        <div className="relative max-w-3xl mx-auto text-center px-4">
          <h2 className="font-display font-700 text-4xl md:text-5xl text-white tracking-tight">Live the city. <span className="text-gradient-rp">Play your way.</span></h2>
          <p className="mt-4 text-white/60">Step into a mysterious futuristic gambling metropolis that remembers everything you do.</p>
          <div className="mt-8 flex flex-wrap gap-4 justify-center">
            <button onClick={() => nav('/city')} className="btn-neon rounded-xl px-7 py-4 font-600 flex items-center gap-2 group">ENTER XINCITY <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" /></button>
            <button onClick={() => nav('/casino')} className="btn-ghost rounded-xl px-7 py-4 font-600">JUST PLAY</button>
          </div>
        </div>
      </section>
    </div>
  );
}
